import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Download, Upload, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { countries, states } from '../../Components/Enums/DataEnums';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from '../../Components/UI/dialog';
import { Button } from '../../Components/UI/button';
import { format } from 'date-fns';
import { getUsersArr, selectThreadFieldPref } from '../../Store/Reducers';
import { isLive } from '../../Utils/Utilities';
import { canShowImportInquiries } from '../../Utils/FeatureFlags';
import useIsMobile from '../../Hooks/useIsMobile';
import { ArrowLeft } from 'lucide-react';

// Get expected columns based on thread type with custom fields
const getExpectedColumns = (threadType, customFields = [], includeSince = true) => {
  const customFieldColumns = customFields.map(field => field.F);
  const sinceColumnName = threadType === 2 ? 'Sale Since' : 'Lead Since';
  const sinceColumn = includeSince ? [sinceColumnName] : [];

  if (threadType === 2) {
    return [
      'Company', 'Contact Name', 'Target Date *', 'Amount', 'Business Potential', 'Due Date', 'Mobile', 'Email',
      'Country', 'State', 'City', 'Source', 'In-charge', 'Product', 'Model',
      'Quantity', 'Notes', ...sinceColumn, 'Next Talk on', 'Next Talk Notes', 'Next Task Type',
      ...customFieldColumns
    ];
  } else {
    return [
      'Company', 'Contact Name', 'Business Potential', 'Expected By', 'Designation', 'Mobile', 'Email',
      'Country', 'State', 'City', 'Source', 'In-charge', 'Product', 'Model',
      'Quantity', 'Notes', ...sinceColumn, 'Next Talk on', 'Next Talk Notes', 'Next Task Type',
      ...customFieldColumns
    ];
  }
};

const getSheetName = (threadType) => {
  return threadType === 2 ? 'Orders' : 'Inquiries';
};

const CONSTANTS = {
  MAX_FIELD_LENGTHS: {
    company: 100,
    contactName: 100,
    designation: 100,
    email: 50,
    city: 50,
    state: 50,
    product: 200,
    quantity: 500,
    notes: 1000,
    source: 50,
    nextTaskNotes: 1000,
    customField: 50 // Added for custom fields
  },
  MOBILE_LENGTHS: {
    india: { min: 10, max: 10 },
    international: { min: 5, max: 15 }
  },
  DATE_FORMAT: 'yyyy-MM-dd'
};

// Utility functions
const DateUtils = {
  convertExcelDateToJSDate: (value) => {
    if (value instanceof Date) return format(value, CONSTANTS.DATE_FORMAT);
    if (typeof value === 'string') return value;
    if (typeof value === 'number') {
      const jsDate = new Date(Date.UTC(1899, 11, 30) + value * 86400 * 1000);
      return format(jsDate, CONSTANTS.DATE_FORMAT);
    }
    return null;
  },

  isValidDate: (dateString) => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  },

  isDateInFuture: (dateString) => {
    const inputDate = new Date(dateString);
    const today = new Date();
    inputDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return inputDate > today;
  }
};

const ValidationUtils = {
  validateEmail: (email) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    return emailRegex.test(email);
  },

  validateMobile: (mobile, isIndian) => {
    if (isIndian) {
      return mobile.length === CONSTANTS.MOBILE_LENGTHS.india.max && !isNaN(mobile);
    }
    const { min, max } = CONSTANTS.MOBILE_LENGTHS.international;
    return mobile.length >= min && mobile.length <= max && !isNaN(mobile);
  },

  validateFieldLength: (value, maxLength) => {
    return value && value.length > maxLength;
  }
};

const DataProcessingUtils = {
  getCountryState: (countryName, stateName) => {
    if (!countryName) {
      return { countryObj: { CountryID: '', CC: '', Country: '' }, stateObj: null };
    }

    const countryObj = countries.find(c =>
      c.Country.toLowerCase() === countryName.toLowerCase()
    ) || { CountryID: '', CC: '', Country: '' };

    let stateObj = null;
    if (stateName && countryObj.CountryID === 1) {
      stateObj = states.find(s =>
        s.State.toLowerCase() === stateName.toLowerCase() && s.CountryID === 1
      ) || null;
    }

    return { countryObj, stateObj };
  },

  findUserByName: (users, userName) => {
    if (!userName || !Array.isArray(users)) return "";

    return users.find(user =>
      user.name?.toLowerCase() === userName.toLowerCase()
    );
  },

  sanitizeValue: (value) => {
    return value ? String(value).trim() : '';
  }
};

const useFileUpload = () => {
  const fileInputRef = useRef(null);

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return { fileInputRef, triggerFileInput };
};

const useExcelValidation = (users, sinceColumnName, threadInfo, customFields) => {
  const validateColumnHeaders = useCallback((headers, expectedCols) => {
    const isOrder = threadInfo?.importType === 'order';
    const requiredColumns = isOrder
      ? ['Company', 'Contact Name', 'Mobile', 'Email', 'Target Date *', 'Amount']
      : ['Company', 'Contact Name', 'Mobile', 'Email'];
    const missingColumns = requiredColumns.filter(col =>
      !headers.includes(col)
    );
    return missingColumns;
  }, [threadInfo]);

  const validateRowData = useCallback((rowData, index, customFields) => {
    const errors = [];
    const sanitized = {};
    const isOrder = threadInfo?.importType === 'order';

    // Sanitize all fields
    Object.keys(rowData).forEach(key => {
      sanitized[key] = DataProcessingUtils.sanitizeValue(rowData[key]);
    });

    // Company validation
    if (!sanitized['Company']) {
      errors.push('Company name is required');
    } else if (ValidationUtils.validateFieldLength(sanitized['Company'], CONSTANTS.MAX_FIELD_LENGTHS.company)) {
      errors.push('Company name is too long');
    }

    // Contact Name validation
    if (!sanitized['Contact Name']) {
      errors.push('Contact person name is required');
    } else if (ValidationUtils.validateFieldLength(sanitized['Contact Name'], CONSTANTS.MAX_FIELD_LENGTHS.contactName)) {
      errors.push('Contact name is too long');
    }

    // Business Potential validation (required for both inquiries and orders)
    if (!sanitized['Business Potential']) {
      errors.push('Business Potential is required');
    } else {
      const bizPotential = parseFloat(sanitized['Business Potential']);
      if (isNaN(bizPotential) || bizPotential < 0) {
        errors.push('Business Potential must be a valid positive number');
      }
    }

    // Due Date validation (optional for both, but label differs)
    const dueDateLabel = isOrder ? 'Due Date' : 'Expected By';
    const dueDateValue = sanitized[dueDateLabel];
    if (dueDateValue) {
      const convertedDate = DateUtils.convertExcelDateToJSDate(dueDateValue);
      if (!DateUtils.isValidDate(convertedDate)) {
        errors.push(`${dueDateLabel} must be in YYYY-MM-DD format`);
      } else {
        const sinceDateValue = sanitized[sinceColumnName];
        if (sinceDateValue) {
          const convertedSinceDate = DateUtils.convertExcelDateToJSDate(sinceDateValue);
          if (DateUtils.isValidDate(convertedSinceDate) && convertedDate < convertedSinceDate) {
            errors.push(`${dueDateLabel} cannot be before ${sinceColumnName}`);
          }
        } else {
          if (DateUtils.isDateInFuture(convertedDate) === false) {
            errors.push(`${dueDateLabel} cannot be in the past`);
          }
        }
      }
    }

    if (isOrder) {
      if (!sanitized['Target Date *']) {
        errors.push('Target date is required for orders');
      } else {
        const convertedDate = DateUtils.convertExcelDateToJSDate(sanitized['Target Date *']);
        if (!DateUtils.isValidDate(convertedDate)) {
          errors.push('Invalid target date format');
        }
      }

      if (!sanitized['Amount']) {
        errors.push('Amount is required for orders');
      } else {
        const amount = parseFloat(sanitized['Amount']);
        if (isNaN(amount) || amount < 0) {
          errors.push('Amount must be a valid positive number');
        }
      }
    }

    // Mobile validation
    const mobile = sanitized['Mobile'];
    if (!mobile) {
      errors.push('Mobile number is required');
    } else {
      const country = sanitized['Country'] || 'India';
      const isIndian = country.toLowerCase() === 'india';
      if (!ValidationUtils.validateMobile(mobile, isIndian)) {
        errors.push(isIndian ? 'Mobile number must be 10 digits' : 'Invalid mobile number format');
      }
    }

    // Email validation
    const email = sanitized['Email'];
    if (!email) {
      errors.push('Email is required');
    } else if (ValidationUtils.validateFieldLength(email, CONSTANTS.MAX_FIELD_LENGTHS.email)) {
      errors.push('Email is too long');
    } else if (!ValidationUtils.validateEmail(email)) {
      errors.push('Invalid email format');
    }

    // Country and State validation
    const { countryObj, stateObj } = DataProcessingUtils.getCountryState(
      sanitized['Country'],
      sanitized['State']
    );

    if (sanitized['Country'] && !countryObj.CountryID) {
      errors.push('Invalid country name');
    }

    if (sanitized['State'] && countryObj.CountryID === 1 && !stateObj) {
      errors.push('Invalid state name for India');
    }

    // In-charge validation
    // if (sanitized['In-charge']) {
    //   const user = DataProcessingUtils.findUserByName(users, sanitized['In-charge']);
    //   if (!user) {
    //     errors.push('Invalid in-charge user');
    //   }
    // }

    // Date validations
    const leadSince = sanitized[sinceColumnName];
    if (leadSince) {
      const convertedDate = DateUtils.convertExcelDateToJSDate(leadSince);
      if (!DateUtils.isValidDate(convertedDate)) {
        errors.push(`Invalid ${sinceColumnName} date format`);
      } else if (DateUtils.isDateInFuture(convertedDate)) {
        errors.push(`${sinceColumnName} date cannot be in future`);
      }
    }

    const nextTalkDT = sanitized['Next Talk on'];
    if (nextTalkDT) {
      const convertedDate = DateUtils.convertExcelDateToJSDate(nextTalkDT);
      if (!DateUtils.isValidDate(convertedDate)) {
        errors.push('Invalid Next Talk date format');
      }
    }

    // Custom fields validation
    if (customFields && customFields.length > 0) {
      customFields.forEach(field => {
        const fieldValue = sanitized[field.F];
        if (fieldValue && ValidationUtils.validateFieldLength(fieldValue, CONSTANTS.MAX_FIELD_LENGTHS.customField)) {
          errors.push(`${field.F} is too long (max 50 characters)`);
        }
      });
    }

    // Field length validations
    const fieldValidations = [
      ...(isOrder ? [] : [{ field: 'Designation', maxLength: CONSTANTS.MAX_FIELD_LENGTHS.designation }]),
      { field: 'City', maxLength: CONSTANTS.MAX_FIELD_LENGTHS.city },
      { field: 'Product', maxLength: CONSTANTS.MAX_FIELD_LENGTHS.product },
      { field: 'Quantity', maxLength: CONSTANTS.MAX_FIELD_LENGTHS.quantity },
      { field: 'Notes', maxLength: CONSTANTS.MAX_FIELD_LENGTHS.notes },
      { field: 'Source', maxLength: CONSTANTS.MAX_FIELD_LENGTHS.source },
      { field: 'Next Talk Notes', maxLength: CONSTANTS.MAX_FIELD_LENGTHS.nextTaskNotes }
    ];

    fieldValidations.forEach(({ field, maxLength }) => {
      if (ValidationUtils.validateFieldLength(sanitized[field], maxLength)) {
        errors.push(`${field} is too long (max ${maxLength} characters)`);
      }
    });

    return {
      errors,
      sanitizedData: sanitized,
      rowIndex: index + 1
    };
  }, [users, sinceColumnName, threadInfo]);

  return { validateColumnHeaders, validateRowData };
};

const styles = {
  container: 'px-4 pb-4 bg-gray-100 sahi-radius flex-1 mt-0',
  listItem: 'flex items-start gap-2',
  listNumber: 'font-semibold text-gray-800 min-w-[20px]',
  listText: 'text-gray-800',
  listHighlight: 'text-[--sahi-orange] font-medium',
  templateButton: 'text-[--sahi-dark-green] hover:text-[--sahi-primary-color] hover:text-opacity-80 underline font-medium flex items-center gap-2 mb-2 p-0',
  buttonContainer: 'flex gap-2 justify-start',
  errorContainer: 'bg-red-50 border border-red-200 rounded-lg p-4 mb-4',
  errorTitle: 'text-red-800 font-semibold mb-2 flex items-center gap-2',
  errorList: 'text-red-700 text-sm space-y-1',
  successMessage: 'bg-green-50 border border-green-200 rounded-lg p-4 text-green-800'
};

const ImportThreadsExcel = ({ open, onClose, threadInfo, triggerButton = true }) => {
  const sinceColumnName = threadInfo?.importType === 'order' ? 'Sale Since' : 'Lead Since';
  const navigate = useNavigate();
  const [excelData, setExcelData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { fileInputRef, triggerFileInput } = useFileUpload();
  const isMobile = useIsMobile();

  const users = useSelector(getUsersArr);
  const thrExtraFields = useSelector(state => state.masterData.thrExtraFields || []);
  const threadTypeID = threadInfo?.importType === 'order' ? 2 : 1;
  const sincePrefEnabled = useSelector(state => selectThreadFieldPref(state, 'Since', threadTypeID));

  // Get custom fields for current thread type
  const customFields = useMemo(() => {
    const typeID = threadInfo?.importType === 'order' ? 2 : 1;
    const threadTypeFields = thrExtraFields.find(item => item.TID === typeID);

    if (threadTypeFields && threadTypeFields.Fields) {
      try {
        const parsedFields = JSON.parse(threadTypeFields.Fields);
        return parsedFields.filter(field => field.IsD === false);
      } catch (e) {
        console.error('Error parsing extra fields:', e);
        return [];
      }
    }
    return [];
  }, [thrExtraFields, threadInfo]);

  const expectedColumns = useMemo(() => {
    return getExpectedColumns(threadInfo?.importType === 'order' ? 2 : 1, customFields, sincePrefEnabled);
  }, [threadInfo, customFields, sincePrefEnabled]);

  const { validateColumnHeaders, validateRowData } = useExcelValidation(users, sinceColumnName, threadInfo, customFields);

  useEffect(() => {
    if (open) {
      setExcelData([]);
      setValidationErrors([]);
    }
  }, [open]);

  const validationChecklist = useMemo(() => {
    const isOrder = threadInfo?.importType === 'order';
    const requiredFields = isOrder
      ? 'Company, Contact Name, Mobile, Email, Target Date, Amount, Business Potential'
      : 'Company, Contact Name, Mobile, Email, Business Potential';

    return [
      {
        number: 1,
        text: <>Column <span className={styles.listHighlight}>names</span> must match the template exactly.</>
      },
      {
        number: 2,
        text: <>Required fields (<span className={styles.listHighlight}>{requiredFields}</span>) must be filled.</>
      },
      {
        number: 3,
        text: <>Indian mobile numbers must be <span className={styles.listHighlight}>10 digits</span> (no +91 prefix).</>
      },
      {
        number: 4,
        text: <>No spaces in <span className={styles.listHighlight}>mobile</span> or <span className={styles.listHighlight}>email</span> fields.</>
      },
      {
        number: 5,
        text: <><span className={styles.listHighlight}>Country, State, In-charge</span> must match system values exactly.</>
      },
      {
        number: 6,
        text: <>Dates must be in <span className={styles.listHighlight}>YYYY-MM-DD</span> format.</>
      },
      {
        number: 7,
        text: <>Business Potential must be a <span className={styles.listHighlight}>numeric value</span>.</>
      },
      {
        number: 8,
        text: <>Extra fields must be <span className={styles.listHighlight}>50 characters or less</span>.</>
      },
      {
        number: 9,
        text: <>Data should be in <span className={styles.listHighlight}>"Inquries"</span> Sheet.</>
      }
    ];
  }, [threadInfo]);

  const downloadTemplate = useCallback(async () => {
    try {
      const isOrder = threadInfo?.importType === 'order';
      const sheetName = isOrder ? 'Orders' : 'Inquiries';
      const fileName = isOrder ? 'SahiClub Template for Orders.xlsx' : 'SahiClub Template for Inquiries.xlsx';

      // Create a new workbook using ExcelJS
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(sheetName);

      // Use getExpectedColumns to ensure consistency
      const typeID = isOrder ? 2 : 1;
      const threadTypeFields = thrExtraFields.find(item => item.TID === typeID);
      const includeSince = sincePrefEnabled ? true : false;

      let activeCustomFields = [];
      if (threadTypeFields && threadTypeFields.Fields) {
        try {
          const parsedFields = JSON.parse(threadTypeFields.Fields);
          activeCustomFields = parsedFields.filter(field => field.IsD === false);
        } catch (e) {
          console.error('Error parsing extra fields:', e);
        }
      }
      const columns = getExpectedColumns(typeID, activeCustomFields, includeSince);
      const nowDT = new Date();
      const targetDT = format(new Date(nowDT.getTime() + 30 * 24 * 60 * 60 * 1000), CONSTANTS.DATE_FORMAT);
      const nextTalkDT = format(new Date(nowDT.getTime() + 7 * 24 * 60 * 60 * 1000), CONSTANTS.DATE_FORMAT);

      // Define sample data row based on thread type
      let sampleRow = [];

      if (isOrder) {
        const dueDT = format(new Date(nowDT.getTime() + 60 * 24 * 60 * 60 * 1000), CONSTANTS.DATE_FORMAT);
        const orderSinceDT = format(new Date(nowDT.getTime() - 30 * 24 * 60 * 60 * 1000), CONSTANTS.DATE_FORMAT);
        sampleRow = includeSince ? [
          'Biziverse Pvt Ltd', 'John Doe', targetDT, '50000', '100000', dueDT, '9876543210', 'john.doe@example.com', 'India', 'Maharashtra',
          'Mumbai', 'Website', 'John Patel', 'ERP Software', '', '10 pcs.', 'Order notes', orderSinceDT, nextTalkDT, 'Follow up on order', 'Follow-Up'
        ] : [
          'Biziverse Pvt Ltd', 'John Doe', targetDT, '50000', '100000', dueDT, '9876543210', 'john.doe@example.com', 'India', 'Maharashtra',
          'Mumbai', 'Website', 'John Patel', 'ERP Software', '', '10 pcs.', 'Order notes', nextTalkDT, 'Follow up on order', 'Follow-Up'
        ];
      } else {
        const dueDT = format(new Date(nowDT.getTime() + 45 * 24 * 60 * 60 * 1000), CONSTANTS.DATE_FORMAT);
        const leadSinceDT = format(new Date(nowDT.getTime() - 60 * 24 * 60 * 60 * 1000), CONSTANTS.DATE_FORMAT);
        sampleRow = includeSince ? [
          'Biziverse Pvt Ltd', 'Jane Smith', '150000', dueDT, 'Purchase Manager', '9876543210', 'jane.smith@example.com', 'India', 'Delhi',
          'Delhi', 'Cold Call', 'John Patel', 'ERP Software', '', '10 pcs.', 'Inquiry notes', leadSinceDT, nextTalkDT, 'Follow up discussion', 'Follow-Up'
        ] : [
          'Biziverse Pvt Ltd', 'Jane Smith', '150000', dueDT, 'Purchase Manager', '9876543210', 'jane.smith@example.com', 'India', 'Delhi',
          'Delhi', 'Cold Call', 'John Patel', 'ERP Software', '', '10 pcs.', 'Inquiry notes', nextTalkDT, 'Follow up discussion', 'Follow-Up'
        ];
      }

      // Add empty values for custom fields
      if (customFields && customFields.length > 0) {
        customFields.forEach(() => {
          sampleRow.push('');
        });
      }

      // Add header row with same height as data row
      const headerRow = worksheet.addRow(columns);
      headerRow.height = 20; // Same height as data row

      // Style header cells - darker and bold
      headerRow.eachCell((cell, colNumber) => {
        const headerText = columns[colNumber - 1];
        const hasAsterisk = headerText.includes('*');

        if (hasAsterisk) {
          // Split text and asterisk for rich text formatting
          const baseText = headerText.replace(' *', '');
          cell.value = {
            richText: [
              { text: baseText, font: { bold: true, size: 11, color: { argb: 'FF000000' }, name: 'Arial' } },
              { text: ' *', font: { bold: true, size: 11, color: { argb: 'FFFF0000' }, name: 'Arial' } }
            ]
          };
        } else {
          cell.value = headerText;
          cell.font = { bold: true, size: 11, color: { argb: 'FF000000' }, name: 'Arial' };
        }

        // Header cell styling
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFFFF' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'medium', color: { argb: 'FF000000' } },
          bottom: { style: 'medium', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
        };
      });

      // Add sample data row with same height
      const dataRow = worksheet.addRow(sampleRow);
      dataRow.height = 20; // Same height as header row

      // Style sample data cells - lighter and italic
      dataRow.eachCell((cell) => {
        cell.font = {
          color: { argb: 'FF000000' },
          size: 11,
          italic: false,
          name: 'Calibri'
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFAFAFA' }
        };
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE8E8E8' } },
          bottom: { style: 'thin', color: { argb: 'FFE8E8E8' } },
          left: { style: 'thin', color: { argb: 'FFE8E8E8' } },
          right: { style: 'thin', color: { argb: 'FFE8E8E8' } }
        };
      });

      // Set column widths
      worksheet.columns.forEach((column, index) => {
        const col = columns[index];
        if (col?.includes('*')) {
          column.width = 22;
        } else if (col?.includes('Notes')) {
          column.width = 30;
        } else if (col?.includes('Date') || col?.includes('Since')) {
          column.width = 18;
        } else if (col?.includes('Quantity')) {
          column.width = 30;
        } else {
          column.width = 20;
        }
      });

      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded.');
    } catch (error) {
      console.error('Error generating template:', error);
      toast.error('Failed to download template. Please try again.');
    }
  }, [threadInfo, thrExtraFields, sincePrefEnabled]);

  const processExcelFile = useCallback(async (file) => {
    setIsProcessing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = getSheetName(threadInfo?.importType === 'order' ? 2 : 1);

      const targetSheet = workbook.SheetNames.find(
        name => name.trim().toLowerCase() === sheetName.toLowerCase()
      )

      if (!targetSheet) {
        throw new Error(`Sheet "${sheetName}" not found. Please use the correct template.`);
      }

      const worksheet = workbook.Sheets[targetSheet];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      if (jsonData.length < 2) {
        throw new Error('Excel file must contain at least a header row and one data row.');
      }

      const headers = jsonData[0].map(h => String(h).trim());
      const missingColumns = validateColumnHeaders(headers, expectedColumns);

      if (missingColumns.length > 0) {
        throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }

      const dataRows = jsonData.slice(1).filter(row =>
        row.some(cell => cell !== '' && cell != null)
      );

      if (dataRows.length === 0) {
        throw new Error('No valid data rows found in the Excel file.');
      }

      const allProcessedData = [];
      const errors = [];

      dataRows.forEach((row, index) => {
        const rowData = {};
        headers.forEach((header, headerIndex) => {
          rowData[header] = row[headerIndex] || '';
        });

        // Handle numbers starting with '+' (e.g., +919876543210)
        const rawMob = String(rowData['Mobile'] || '').trim();
        if (rawMob.startsWith('+')) {
          const cleanDigits = rawMob.replace(/\D/g, '');
          const matched = [...countries]
            .sort((a, b) => String(b.CC).length - String(a.CC).length)
            .find(c => cleanDigits.startsWith(String(c.CC)));

          if (matched) {
            rowData['Mobile'] = cleanDigits.substring(String(matched.CC).length);
            rowData['Country'] = matched.Country;
          }
        }

        const validation = validateRowData(rowData, index, customFields);

        if (validation.errors.length > 0) {
          errors.push({
            rowNumber: validation.rowIndex,
            company: validation.sanitizedData['Company'] || 'Unnamed Company',
            errors: validation.errors
          });
        }

        const { countryObj, stateObj } = DataProcessingUtils.getCountryState(
          validation.sanitizedData['Country'],
          validation.sanitizedData['State']
        );

        const user = DataProcessingUtils.findUserByName(users, validation.sanitizedData['In-charge']);

        // Process custom fields
        const customFieldsData = [];
        if (customFields && customFields.length > 0) {
          customFields.forEach(field => {
            const fieldValue = validation.sanitizedData[field.F];
            if (fieldValue && fieldValue.trim()) {
              customFieldsData.push({
                FID: field.FID,
                V: fieldValue.trim().substring(0, 50)
              });
            }
          });
        }

        // Map due date based on thread type - both use same DueDT field
        const dueDateLabel = threadInfo?.importType === 'order' ? 'Due Date' : 'Expected By';
        const dueDateValue = DateUtils.convertExcelDateToJSDate(validation.sanitizedData[dueDateLabel]);

        const processedRow = {
          BizName: validation.sanitizedData['Company'],
          ContactName: validation.sanitizedData['Contact Name'],
          Mobile: validation.sanitizedData['Mobile'],
          Email: validation.sanitizedData['Email'],
          Country: countryObj.Country || 'India',
          CountryID: countryObj.CountryID || 1,
          CC: countryObj.CC || '',
          State: stateObj?.State || validation.sanitizedData['State'] || '',
          StateID: stateObj?.StateID || 0,
          City: validation.sanitizedData['City'],
          Source: validation.sanitizedData['Source'],
          Product: validation.sanitizedData['Product'],
          Model: validation.sanitizedData['Model'] || '',
          Quantity: validation.sanitizedData['Quantity'],
          Notes: validation.sanitizedData['Notes'],
          POCUserID: user?.userID || 0,
          POCUserName: user?.name || validation.sanitizedData['In-charge'] || '',
          SinceDT: DateUtils.convertExcelDateToJSDate(validation.sanitizedData[sinceColumnName]) || new Date().toISOString().split('T')[0],
          NextTalkDT: DateUtils.convertExcelDateToJSDate(validation.sanitizedData['Next Talk on']),
          NextTalkNotes: validation.sanitizedData['Next Talk Notes'],
          NextTaskType: validation.sanitizedData['Next Task Type'] || '',
          TypeID: threadInfo?.importType === 'order' ? 2 : 1,
          ConnID: 0,
          Type: threadInfo?.importType === 'order' ? 'ORD' : 'INQ',
          MainQty: 0,
          Items: '[]',
          DueDT: threadInfo?.importType === 'order' ? DateUtils.convertExcelDateToJSDate(validation.sanitizedData['Target Date *']) : (dueDateValue || null),
          CurrStage: 'New',
          ExtraFields: '[]',
          CustomFields: JSON.stringify(customFieldsData),
          BizProspect: parseFloat(validation.sanitizedData['Business Potential']) || 0,
          Reference: '',
          Stage: 1,
          SrcActionID: '',
          NextApptType: 'Other',
          Contacts: '[]'
        };

        if (threadInfo?.importType !== 'order') {
          processedRow.Designation = validation.sanitizedData['Designation'];
        }

        if (threadInfo?.importType === 'order') {
          processedRow.Amount = parseFloat(validation.sanitizedData['Amount']) || 0;
        }

        allProcessedData.push(processedRow);
      });

      setExcelData(allProcessedData);
      setValidationErrors(errors);

      if (allProcessedData.length > 0) {
        const message = errors.length === 0
          ? `Successfully processed ${allProcessedData.length} rows. Ready to import.`
          : `Processed ${allProcessedData.length} rows with ${errors.length} validation errors. Please review and correct.`;

        toast.success(message);

        navigate('/correct-excel-threads', {
          state: {
            excelData: allProcessedData,
            errors: errors,
            threadInfo: threadInfo,
            inquiryData: [],
            loading: false,
            error: null,
            customFields: customFields
          }
        });
        onClose();
      } else {
        toast.error('No data found to process.');
      }

    } catch (error) {
      toast.error(error.message || 'Failed to process Excel file');
      setExcelData([]);
      setValidationErrors([]);
    } finally {
      setIsProcessing(false);
    }
  }, [validateColumnHeaders, validateRowData, users, navigate, onClose, customFields, expectedColumns]);

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) {
      toast.error('No file selected. Please upload a valid Excel file.');
      return;
    }

    if (!file.name.match(/\.(xlsx|xls)$/)) {
      toast.error('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    processExcelFile(file);
  }, [processExcelFile]);

  const handleImportClick = useCallback(() => {
    if (isMobile) {
      toast.error('Please switch to desktop to import inquiries from Excel.');
      return;
    }
    triggerFileInput();
  }, [isMobile, triggerFileInput]);

  if (!open || !onClose || !canShowImportInquiries()) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {triggerButton && <DialogTrigger asChild />}
      <DialogContent className="w-[560px] p-0">
        {isMobile ? (
          <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
            <DialogClose className=" p-1 sahi-radius">
              <ArrowLeft className="h-5 w-5" />
            </DialogClose>

            <div className="flex items-center gap-2">
              <DialogTitle className="font-semibold text-lg">
                Import {threadInfo?.importType == 'order' ? 'Orders' : 'Inquiries'} from Excel
              </DialogTitle>
            </div>
          </DialogHeader>
        ) : (
          <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
            <div className="flex items-center gap-2">
              <DialogTitle className="font-semibold text-lg">
                Import {threadInfo?.importType == 'order' ? 'Orders' : 'Inquiries'} from Excel
              </DialogTitle>
            </div>

            <DialogClose className="p-1 sahi-radius">
              <X className="h-5 w-5" />
            </DialogClose>
          </DialogHeader>
        )}

        <div className={styles.container}>
          <div className="mb-6">
            <h3 className="text-base font-semibold text-gray-800 mb-3">
              Pre-import Checklist
            </h3>
            <div className="space-y-2 text-sm">
              {validationChecklist.map(item => (
                <div key={item.number} className={styles.listItem}>
                  <span className={styles.listNumber}>{item.number}.</span>
                  <div className={styles.listText}>{item.text}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <Button
              variant="link"
              onClick={downloadTemplate}
              className={styles.templateButton}
              disabled={isProcessing}
            >
              <Download className="h-4 w-4 text-[--sahi-primary-color]" />
              Download Template
            </Button>
          </div>

          {validationErrors.length > 0 && (
            <div className={styles.errorContainer}>
              <div className={styles.errorTitle}>
                <AlertCircle className="h-5 w-5" />
                Validation Errors ({validationErrors.length} rows)
              </div>
              <div className={styles.errorList}>
                {validationErrors.slice(0, 10).map((error, index) => (
                  <div key={index} className="border-l-2 border-red-300 pl-3 py-1">
                    <div className="font-medium">
                      Row {error.rowNumber}: {error.company}
                    </div>
                    <ul className="ml-4 list-disc">
                      {error.errors.map((err, errIndex) => (
                        <li key={errIndex}>{err}</li>
                      ))}
                    </ul>
                  </div>
                ))}
                {validationErrors.length > 10 && (
                  <div className="text-red-600 font-medium mt-2">
                    ... and {validationErrors.length - 10} more errors
                  </div>
                )}
              </div>
            </div>
          )}

          {excelData.length > 0 && validationErrors.length === 0 && (
            <div className={styles.successMessage}>
              ✓ Successfully processed {excelData.length} rows. Ready for import.
            </div>
          )}

          <div className={styles.buttonContainer}>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={handleImportClick}
              disabled={isProcessing}
              className="flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Import Excel
                </>
              )}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={isProcessing}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportThreadsExcel;